/**
 * Delaware C-corp annual franchise tax, 8 Del. C. section 503, plus the annual
 * report fee (8 Del. C. section 391(a)(18)). All amounts are integer cents.
 *
 * Moved from `apps/api/src/state-tax/delaware-franchise-tax.ts`, which is
 * authoritative; the app's `share-count.vo.ts` was a hand copy of it and never
 * disagreed on a figure. Ported without changing the arithmetic.
 *
 * The tax is the LESSER of two methods:
 *
 * - Authorized Shares method: up to 5,000 shares $175; 5,001-10,000 shares
 *   $250; each additional 10,000 shares or portion thereof adds $85.
 * - Assumed Par Value Capital method: assumed par = total gross assets /
 *   total issued shares. Authorized shares with a par value LESS than the
 *   assumed par (and no-par shares) are multiplied by the assumed par; shares
 *   with a par value EQUAL TO OR GREATER than the assumed par are multiplied by
 *   their stated par. The sum is the assumed par value capital (APVC); $400 per
 *   $1,000,000 of APVC or portion thereof, minimum $400. It is only computable
 *   when gross assets and issued shares are known. Our data model carries one
 *   class of stock, so the optional `parValueMicros` is that class's par
 *   (integer micro-dollars: $0.00001 = 10); absent means no-par.
 *
 * The tax is capped at $200,000. The $250,000 cap for large corporate filers
 * (listed companies with $750M or more in revenue or assets, or more than
 * 5,000,000 authorized shares in a group of related corporations) needs data
 * we do not collect, so it applies only when the caller sets
 * `largeCorporateFiler`.
 *
 * With no gross assets or issued shares the APVC figure is unknown. When the
 * Authorized Shares figure is above the $400 APVC floor, APVC could plausibly
 * be lower, so the result flags `needsApvcInputs` instead of quietly billing a
 * possibly-too-high figure; callers must collect the inputs (or an explicit
 * amount) before invoicing.
 */

export const DE_ANNUAL_REPORT_FEE_CENTS = 5_000;
export const DE_FRANCHISE_TAX_MAX_CENTS = 20_000_000;
export const DE_FRANCHISE_TAX_LARGE_FILER_CAP_CENTS = 25_000_000;

const ASM_UP_TO_5K_CENTS = 17_500;
const ASM_UP_TO_10K_CENTS = 25_000;
const ASM_PER_ADDITIONAL_10K_CENTS = 8_500;
const APVC_PER_MILLION_CENTS = 40_000;
const APVC_MIN_CENTS = 40_000;
const APVC_MILLION_IN_CENTS = 100_000_000n;
/** 1 cent = 10,000 micro-dollars. */
const MICROS_PER_CENT = 10_000n;

export type FranchiseTaxMethod =
  "authorized_shares" | "assumed_par_value_capital";

export interface FranchiseTaxInput {
  authorizedShares: number;
  /** Total gross assets in cents, from the most recent balance sheet. */
  grossAssetsCents?: number | null;
  /** Total issued shares. */
  issuedShares?: number | null;
  /**
   * Stated par value per share in integer micro-dollars ($0.00001 = 10,
   * $1.00 = 1,000,000). Absent, zero or fractional means no-par (the assumed
   * par applies to every authorized share).
   */
  parValueMicros?: number | null;
  /** Use the $250,000 cap of a large corporate filer. Default false. */
  largeCorporateFiler?: boolean;
}

export interface FranchiseTaxResult {
  /** Authorized Shares figure, capped. */
  authorizedSharesTax: number;
  /** APVC figure, capped; null when gross assets or issued shares are missing. */
  apvcTax: number | null;
  /** The franchise tax: the lower of the two, capped. */
  tax: number;
  method: FranchiseTaxMethod;
  /** The $50 annual report fee, always due with the franchise tax. */
  reportFee: number;
  /** tax + reportFee. */
  total: number;
  /** True when APVC inputs are missing and the APVC method could be lower. */
  needsApvcInputs: boolean;
}

function authorizedSharesMethodCents(authorizedShares: number): number {
  if (authorizedShares <= 5_000) return ASM_UP_TO_5K_CENTS;
  if (authorizedShares <= 10_000) return ASM_UP_TO_10K_CENTS;
  const extraBlocks = Math.ceil((authorizedShares - 10_000) / 10_000);
  return ASM_UP_TO_10K_CENTS + extraBlocks * ASM_PER_ADDITIONAL_10K_CENTS;
}

function isValidParValueMicros(
  value: number | null | undefined,
): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function hasApvcInputs(input: FranchiseTaxInput): boolean {
  return (
    typeof input.grossAssetsCents === "number" &&
    Number.isFinite(input.grossAssetsCents) &&
    input.grossAssetsCents >= 0 &&
    typeof input.issuedShares === "number" &&
    Number.isInteger(input.issuedShares) &&
    input.issuedShares > 0
  );
}

function assumedParValueCapitalMethodCents(
  authorizedShares: number,
  grossAssetsCents: number,
  issuedShares: number,
  parValueMicros: number | null | undefined,
  capCents: number,
): number {
  // BigInt keeps assets x authorized shares exact past 2^53.
  const assets = BigInt(Math.round(grossAssetsCents));
  const issued = BigInt(issuedShares);
  let apvcCents: bigint;
  // Shares with par >= assumed par count at their stated par; shares with a
  // lower par (or no par) count at the assumed par. Compare par to assumed par
  // exactly: par >= assets / issued (in micro-dollars) <=> par x issued >= assets x 10,000.
  if (
    isValidParValueMicros(parValueMicros) &&
    BigInt(parValueMicros) * issued >= assets * MICROS_PER_CENT
  ) {
    const numerator = BigInt(authorizedShares) * BigInt(parValueMicros);
    apvcCents = (numerator + MICROS_PER_CENT - 1n) / MICROS_PER_CENT;
  } else {
    const numerator = assets * BigInt(authorizedShares);
    apvcCents = (numerator + issued - 1n) / issued;
  }
  const blocks =
    (apvcCents + APVC_MILLION_IN_CENTS - 1n) / APVC_MILLION_IN_CENTS;
  const taxBig = blocks * BigInt(APVC_PER_MILLION_CENTS);
  const tax = Number(taxBig > BigInt(capCents) ? BigInt(capCents) : taxBig);
  return Math.max(APVC_MIN_CENTS, tax);
}

export function franchiseTax(input: FranchiseTaxInput): FranchiseTaxResult {
  const cap = input.largeCorporateFiler
    ? DE_FRANCHISE_TAX_LARGE_FILER_CAP_CENTS
    : DE_FRANCHISE_TAX_MAX_CENTS;
  const authorizedSharesTax = Math.min(
    authorizedSharesMethodCents(input.authorizedShares),
    cap,
  );
  const reportFee = DE_ANNUAL_REPORT_FEE_CENTS;

  if (!hasApvcInputs(input)) {
    return {
      authorizedSharesTax,
      apvcTax: null,
      tax: authorizedSharesTax,
      method: "authorized_shares",
      reportFee,
      total: authorizedSharesTax + reportFee,
      needsApvcInputs: authorizedSharesTax > APVC_MIN_CENTS,
    };
  }

  const apvcTax = Math.min(
    assumedParValueCapitalMethodCents(
      input.authorizedShares,
      input.grossAssetsCents as number,
      input.issuedShares as number,
      input.parValueMicros,
      cap,
    ),
    cap,
  );
  const useApvc = apvcTax < authorizedSharesTax;
  const tax = useApvc ? apvcTax : authorizedSharesTax;
  return {
    authorizedSharesTax,
    apvcTax,
    tax,
    method: useApvc ? "assumed_par_value_capital" : "authorized_shares",
    reportFee,
    total: tax + reportFee,
    needsApvcInputs: false,
  };
}

/** A share count is a positive integer; anything else is null. */
export function parseShareCount(value: number): number | null {
  return Number.isInteger(value) && value > 0 ? value : null;
}

/** At or below 5,000 authorized shares: the $175 minimum bracket. */
export function isMinimumTaxBracket(authorizedShares: number): boolean {
  return authorizedShares <= 5_000;
}

/** The Authorized Shares figure has reached the $200,000 cap. */
export function isMaximumTaxBracket(authorizedShares: number): boolean {
  return (
    authorizedSharesMethodCents(authorizedShares) >= DE_FRANCHISE_TAX_MAX_CENTS
  );
}

/**
 * True when fewer than 10% of the authorized shares are issued. That is a
 * common sign the customer typed the wrong count, and Delaware bills on the
 * authorized figure when the APVC inputs look off.
 */
export function issuedRatioWarning(
  issued: number,
  authorized: number,
): boolean {
  if (!Number.isFinite(issued) || !Number.isFinite(authorized)) return false;
  if (issued <= 0 || authorized <= 0) return false;
  return issued / authorized < 0.1;
}
